'use client'

import { useRef, useState, useEffect } from 'react'
import Image from 'next/image'
import {
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from 'motion/react'

const MotionImage = motion(Image)

export function CaseStudyHeroMedia({
  image,
  video,
  quality = 90,
  className = '',
  sizes,
  priority,
}) {
  const containerRef = useRef(null)
  const videoRef = useRef(null)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [isPageLoaded, setIsPageLoaded] = useState(false)

  // Grayscale scroll effect
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 65%', 'end 35%'],
  })
  const grayscale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0, 1])
  const filter = useMotionTemplate`grayscale(${grayscale})`

  // Wait for page to fully load before loading video
  useEffect(() => {
    if (!video) return

    const handleLoad = () => {
      setIsPageLoaded(true)
    }

    // Check if already loaded
    if (document.readyState === 'complete') {
      setIsPageLoaded(true)
    } else {
      window.addEventListener('load', handleLoad)
      return () => window.removeEventListener('load', handleLoad)
    }
  }, [video])

  // Load video after page is ready
  useEffect(() => {
    if (!isPageLoaded || !video || !videoRef.current) return

    const videoElement = videoRef.current

    const handleCanPlay = () => {
      setIsVideoLoaded(true)
      videoElement.play().catch(() => {
        // Autoplay might be blocked, that's okay
      })
    }

    videoElement.addEventListener('canplay', handleCanPlay)
    videoElement.load()

    return () => {
      videoElement.removeEventListener('canplay', handleCanPlay)
    }
  }, [isPageLoaded, video])

  // If no video, render standard image behavior
  if (!video) {
    return (
      <div ref={containerRef} className="group relative">
        <MotionImage
          alt=""
          style={{ filter }}
          src={image.src}
          quality={quality}
          className={className}
          sizes={sizes}
          priority={priority}
        />
        <div
          className="pointer-events-none absolute top-0 left-0 w-full opacity-0 transition duration-300 group-hover:opacity-100"
          aria-hidden="true"
        >
          <Image
            alt=""
            src={image.src}
            quality={quality}
            className={className}
            sizes={sizes}
          />
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="group relative overflow-hidden">
      {/* Placeholder Image - visible until video loads */}
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: isVideoLoaded ? 0 : 1 }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
        className="absolute inset-0 z-10"
        style={{ filter }}
      >
        <Image
          alt=""
          src={image.src}
          quality={quality}
          className={className}
          sizes={sizes}
          priority={priority}
          fill
          style={{ objectFit: 'cover' }}
        />
      </motion.div>

      {/* Video - loads after page is ready */}
      <motion.video
        ref={videoRef}
        style={{ filter }}
        className={`${className} aspect-video w-full object-cover`}
        muted
        loop
        playsInline
        preload="none"
        initial={{ opacity: 0 }}
        animate={{ opacity: isVideoLoaded ? 1 : 0 }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
      >
        <source src={video} type="video/mp4" />
      </motion.video>

      {/* Hover state - removes grayscale filter on hover */}
      <motion.div
        className="pointer-events-none absolute inset-0 z-20 opacity-0 transition duration-300 group-hover:opacity-100"
        aria-hidden="true"
      >
        {isVideoLoaded ? (
          <video
            className={`${className} aspect-video h-full w-full object-cover`}
            muted
            loop
            playsInline
            autoPlay
          >
            <source src={video} type="video/mp4" />
          </video>
        ) : (
          <Image
            alt=""
            src={image.src}
            quality={quality}
            className={className}
            sizes={sizes}
            fill
            style={{ objectFit: 'cover' }}
          />
        )}
      </motion.div>
    </div>
  )
}
